import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  Article as ArticleIcon,
  Campaign as CampaignIcon,
  Notifications as NotificationIcon,
  Description as DescriptionIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { DocumentUpdateNotification } from '../hooks/useNotifications';

interface NotificationModalProps {
  open: boolean;
  notification: DocumentUpdateNotification | null;
  onClose: () => void;
  onAcknowledge: () => void;
  loading?: boolean;
  error?: string | null;
}

export const NotificationModal: React.FC<NotificationModalProps> = ({
  open,
  notification,
  onClose,
  onAcknowledge,
  loading = false,
  error
}) => {
  if (!notification) {
    return null;
  }

  const isSystemNotification = notification.changesSummary?.isSystemNotification;
  const hasItems = notification.items && notification.items.length > 0;
  
  const getNotificationIcon = () => {
    if (isSystemNotification) {
      return <CampaignIcon color="info" sx={{ fontSize: 32 }} />;
    }
    
    return <ArticleIcon color={notification.updateType === 'MAJOR' ? 'warning' : 'primary'} sx={{ fontSize: 32 }} />;
  };

  const getNotificationTitle = () => {
    if (isSystemNotification) {
      return notification.changesSummary?.title || 'System-Benachrichtigung';
    }
    
    return `${notification.document.reference} wurde aktualisiert`;
  };

  const getNotificationDescription = () => {
    if (isSystemNotification) {
      return notification.changesSummary?.description || 'Neue Systemankündigung verfügbar';
    }
    
    const updateTypeText = notification.updateType === 'MAJOR' ? 'Wichtige Änderung' : 'Aktualisierung';
    return `${updateTypeText} von Version ${notification.previousVersion} auf ${notification.newVersion}`;
  };

  const getItemIcon = (itemType: string | undefined) => {
    switch (itemType) {
      case 'DOCUMENT_NEW':
        return <AddIcon fontSize="small" color="success" />;
      case 'DOCUMENT_DELETE':
        return <DeleteIcon fontSize="small" color="error" />;
      default:
        return <EditIcon fontSize="small" color="primary" />;
    }
  };

  const getItemLabel = (itemType: string | undefined) => {
    switch (itemType) {
      case 'DOCUMENT_NEW':
        return 'Neu';
      case 'DOCUMENT_DELETE':
        return 'Entfernt';
      default:
        return 'Aktualisiert';
    }
  };

  const getItemColor = (itemType: string | undefined) => {
    switch (itemType) {
      case 'DOCUMENT_NEW':
        return 'success';
      case 'DOCUMENT_DELETE':
        return 'error';
      default:
        return 'primary';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
          minHeight: '300px',
          height: 'auto',
          maxWidth: '680px' // About 20% narrower than default md (600px)
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            {getNotificationIcon()}
            <Box>
              <Typography 
                variant="h6" 
                component="h2"
                sx={{ 
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  hyphens: 'auto',
                  maxWidth: '400px'
                }}
              >
                {isSystemNotification ? getNotificationTitle() : `Neues Update: ${getNotificationTitle()}`}
              </Typography>
              {notification.updateType === 'MAJOR' && (
                <Chip label="Neue Version" color="warning" size="small" sx={{ mt: 0.5 }} />
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2, flex: 1, overflow: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              mb: 2,
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6
            }}
          >
            {getNotificationDescription()}
          </Typography>

          {hasItems && (
             <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Betroffene Dokumente
                </Typography>
                <List dense sx={{ 
                    bgcolor: 'grey.50', 
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    maxHeight: '250px',
                    overflow: 'auto'
                }}>
                  {notification.items!.map((item, index) => (
                    <React.Fragment key={index}>
                      <ListItem alignItems="flex-start">
                        <Box sx={{ mr: 2, mt: 0.5, display: 'flex', alignItems: 'center' }}>
                            {getItemIcon(item.itemType)}
                        </Box>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <Typography variant="body2" fontWeight={600}>
                                    {item.document.reference}
                                </Typography>
                                <Typography variant="body2" component="span">
                                    - {item.document.name}
                                </Typography>
                                <Chip 
                                    label={getItemLabel(item.itemType)} 
                                    size="small" 
                                    color={getItemColor(item.itemType) as any} 
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '0.625rem' }} 
                                />
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                {/* {item.changeSummary || (item.updateType === 'MAJOR' ? 'Wichtige inhaltliche Änderung' : 'Kleinere Aktualisierung')} */}
                                
                                {item.updateType === 'MAJOR' && item.itemType === 'DOCUMENT_UPDATE' && (
                                    <Chip label="Major" size="small" color="warning" sx={{ height: 16, fontSize: '0.625rem', ml: 1 }} />
                                )}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < notification.items!.length - 1 && <Divider component="li" variant="inset" sx={{ ml: 7 }} />}
                    </React.Fragment>
                  ))}
                </List>
             </Box>
          )}

          {!isSystemNotification && !hasItems && (
            <Box sx={{ 
              p: 2, 
              bgcolor: 'grey.50', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200'
            }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Dokument Details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                <strong>Name:</strong> {notification.document.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                <strong>Referenz:</strong> {notification.document.reference}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                <strong>Typ:</strong> {notification.document.type}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                <strong>Zuständigkeit:</strong> {notification.document.jurisdiction}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          disabled={loading}
        >
          Später
        </Button>
        <Button 
          onClick={onAcknowledge}
          variant="contained"
          disabled={loading}
          startIcon={loading ? null : <NotificationIcon />}
        >
          {loading ? 'Wird verarbeitet...' : 'Verstanden'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
